import { t } from "i18next";
import { AppRoutes, getDynamicAppRoutes } from "@/app/routes";
import { NavItemsMap } from "./types";
import { PERMISSIONS } from "@/shared/constants/permissions";
import type { RuntimeConfig } from "@/shared/types/runtimeConfig.types";
import type { User } from "@/shared/types/auth.types";
import { isCenterUser, isSubcontractorUser } from "@/shared/utils/centerUser";
import {
  Activity,
  AlertTriangle,
  BarChart2,
  Bell,
  BookOpen,
  CalendarRange,
  ClipboardList,
  Clock3,
  CreditCard,
  Database,
  FileCog,
  FileSpreadsheet,
  Flag,
  FolderArchive,
  Gauge,
  Globe,
  LayoutDashboard,
  ListChecks,
  Map,
  MapPinned,
  MessageSquare,
  Monitor,
  Moon,
  Route,
  ScanSearch,
  Settings2,
  Shield,
  SlidersHorizontal,
  Sun,
  Truck,
  Upload,
  UserCog,
  UserPlus,
  Users,
  Wrench,
} from "lucide-react";

export const menuRoutePrefixes: Record<string, string[]> = {
  operations: [
    "/operations",
    "/schedules",
    "/trips",
    "/subscriptions",
    "/archive-schedules",
    "/archive-trips",
    "/expenses",
  ],
  admin: ["/admin", "/providers", "/provider-rates", "/trip-groups", "/fixed-routes"],
  tools: ["/tools", "/rider-policy-violations", "/bulk-import"],
  system: ["/system"],
};

export const getNavItems = (
  runtimeConfig?: RuntimeConfig | null,
  user?: User | null,
): NavItemsMap => {
  // Build all dynamic (host-relative) external URLs from runtime config
  const dynamic = getDynamicAppRoutes(runtimeConfig);

  // Mirrors Rails nav.rb:81 — Eligibility link requires the config flag AND
  // the user must NOT be a Center user.
  const eligibilityEnabled =
    runtimeConfig?.['component.eligibility.enabled'] === '1' ||
    runtimeConfig?.['component.eligibility.enabled'] === 1 ||
    runtimeConfig?.['component.eligibility.enabled'] === true ||
    runtimeConfig?.['component.eligibility.enabled'] === 'true';
  const showEligibility = eligibilityEnabled && !isCenterUser(user);

  // Mirrors Rails nav.rb:102 — Surcharges admin page is only enabled when the
  // component.negotiatedProviderRates.enabled config is on (default off). The
  // Provider Rates page is always visible.
  const negotiatedProviderRatesEnabled =
    runtimeConfig?.['component.negotiatedProviderRates.enabled'] === '1' ||
    runtimeConfig?.['component.negotiatedProviderRates.enabled'] === 1 ||
    runtimeConfig?.['component.negotiatedProviderRates.enabled'] === true ||
    runtimeConfig?.['component.negotiatedProviderRates.enabled'] === 'true';

  // Mirrors Rails nav.rb:167-168, 277-280 — Reimbursement / Expense / Reimbursement
  // Event nav entries depend on component.mileageReimbursement.enabled (default off).
  const mileageReimbursementEnabled =
    runtimeConfig?.['component.mileageReimbursement.enabled'] === '1' ||
    runtimeConfig?.['component.mileageReimbursement.enabled'] === 1 ||
    runtimeConfig?.['component.mileageReimbursement.enabled'] === true ||
    runtimeConfig?.['component.mileageReimbursement.enabled'] === 'true';

  // Mirrors Rails nav.rb:105-115, 285-291, 319-321 — Inspection Checklist /
  // Company / Vehicle / Archive Inspection nav entries are gated by the
  // comp_vehicle_inspection eco-component (component.inspection.enabled).
  // Use a permissive truthy check so backend variations ("Yes", "TRUE", "1",
  // true) all resolve to enabled — matches runtimeConfigService.isFeatureEnabled.
  const rawInspection = runtimeConfig?.['component.inspection.enabled'];
  const inspectionEnabled =
    rawInspection === true ||
    rawInspection === 1 ||
    (typeof rawInspection === 'string' &&
      ['1', 'true', 'yes'].includes(rawInspection.trim().toLowerCase()));

  // Mirrors Rails nav.rb:378-386 — Rider Policy Violation / Potential Violation
  // nav entries are gated by the comp_noshow eco-component
  // (component.noShow.enabled). Same permissive truthy handling.
  const rawNoShow = runtimeConfig?.['component.noShow.enabled'];
  const noShowEnabled =
    rawNoShow === true ||
    rawNoShow === 1 ||
    (typeof rawNoShow === 'string' &&
      ['1', 'true', 'yes'].includes(rawNoShow.trim().toLowerCase()));

  // EcoVision / Vestige link is only shown when the comp_vestige eco-component
  // (component.vestige.enabled) is on — mirrors the run-status livestream gate
  // in Rails helpers/run_statuses_helper.rb:47.
  const rawVestige = runtimeConfig?.['component.vestige.enabled'];
  const vestigeEnabled =
    rawVestige === true ||
    rawVestige === 1 ||
    (typeof rawVestige === 'string' &&
      ['1', 'true', 'yes'].includes(rawVestige.trim().toLowerCase()));

  return {
  operations: [
    {
      key: "op-overview",
      to: "#",
      label: t("navbar.operations.overview"),
      submenu: [
        { key: "op-dashboard", to: AppRoutes.operations.dashboard, label: t("navbar.operations.dashboard"), icon: LayoutDashboard, permission: PERMISSIONS.DASHBOARD_VIEW },
        {
          key: "op-run-status",
          to: AppRoutes.operations.runStatus,
          label: t("navbar.operations.runStatus"),
          icon: Gauge,
          permission: PERMISSIONS.RUN_VIEW,
        },
        {
          key: "op-on-time-performance",
          to: AppRoutes.operations.onTimePerformance,
          label: t("navbar.operations.onTimePerformance"),
          icon: Activity,
          permission: PERMISSIONS.TRIP_VIEW,
        },
        {
          key: "op-late-trips",
          to: AppRoutes.operations.lateTrips,
          label: t("navbar.operations.lateTrips"),
          icon: Clock3,
          permission: PERMISSIONS.TRIP_VIEW,
        },
        { key: "op-alerts", to: AppRoutes.operations.alerts, label: t("navbar.operations.alerts"), icon: AlertTriangle, permission: PERMISSIONS.ALERT_VIEW },
        { key: "op-messages", to: AppRoutes.operations.messages, label: t("navbar.operations.messages"), icon: MessageSquare, permission: PERMISSIONS.ALERT_VIEW },
        // Mirrors Rails nav.rb:252 — EcoVision/Vestige link hidden from Center users.
        // Also gated on component.vestige.enabled so sites without Vestige
        // cameras don't see the link at all.
        ...(vestigeEnabled && !isCenterUser(user)
          ? [{ key: "op-ecovision", label: "EcoVision", to: dynamic.ecovision, external: true }]
          : []),
      ],
    },
    {
      key: "op-schedule-management",
      to: "#",
      label: t("navbar.operations.scheduleManagement"),
      submenu: [
        { key: "op-schedules", to: AppRoutes.schedules, label: t("navbar.operations.schedules"), icon: CalendarRange, permission: PERMISSIONS.METAROUTE_VIEW },
        { key: "op-trips", to: AppRoutes.trips, label: t("navbar.operations.trips"), icon: Route, permission: PERMISSIONS.TRIP_VIEW },
        { key: "op-subscriptions", to: AppRoutes.subscriptions, label: t("navbar.operations.subscriptions"), icon: ListChecks, permission: PERMISSIONS.SUBSCRIPTION_VIEW },
      ],
    },
    {
      key: "op-financial",
      to: "#",
      label: t("navbar.operations.financial"),
      submenu: [
        ...(mileageReimbursementEnabled
          ? [
              {
                key: "op-reimbursements",
                to: AppRoutes.reimbursements,
                label: t("navbar.operations.reimbursements"),
                icon: CreditCard,
                permission: PERMISSIONS.REIMBURSEMENT_VIEW,
              },
              { key: "op-expenses", to: AppRoutes.expenses, label: t("navbar.operations.expenses"), icon: ClipboardList, permission: PERMISSIONS.EXPENSE_VIEW },
            ]
          : []),
      ],
    },
    {
      key: "op-archive",
      to: "#",
      label: t("navbar.operations.archive"),
      submenu: [
        {
          key: "op-archive-schedules",
          to: AppRoutes.archiveSchedules,
          label: t("navbar.operations.archiveSchedules"),
          icon: FolderArchive,
          permission: PERMISSIONS.ARCHIVE_ROUTE_VIEW,
        },
        { key: "op-archive-trips", to: AppRoutes.archiveTrips, label: t("navbar.operations.archiveTrips"), icon: FolderArchive, permission: PERMISSIONS.ARCHIVE_TRIP_VIEW },
        ...(inspectionEnabled
          ? [{
              key: "op-inspection-lists",
              to: AppRoutes.operations.inspectionLists,
              label: t("navbar.operations.inspectionLists"),
              icon: ScanSearch,
              permission: PERMISSIONS.ARCHIVE_INSPECTION_CHECKLIST_VIEW,
            }]
          : []),
      ],
    },
  ],
  administration: [
    {
      key: "admin-resources",
      to: "#",
      label: t("navbar.admin.resources"),
      submenu: [
        { key: "admin-providers", to: AppRoutes.admin.providers, label: t("navbar.admin.providers"), icon: Truck, permission: PERMISSIONS.COMPANY_VIEW },
        { key: "admin-runs", to: AppRoutes.admin.runs, label: t("navbar.admin.runs"), icon: Route, permission: PERMISSIONS.RUN_VIEW },
        { key: "admin-vehicles", to: AppRoutes.admin.vehicles, label: t("navbar.admin.vehicles"), icon: Truck, permission: PERMISSIONS.VEHICLE_VIEW },
        { key: "admin-drivers", to: AppRoutes.admin.drivers, label: t("navbar.admin.drivers"), icon: Users, permission: PERMISSIONS.DRIVER_VIEW },
      ],
    },
    {
      key: "admin-client-management",
      to: "#",
      label: t("navbar.admin.clientManagement"),
      submenu: [
        { key: "admin-clients", to: AppRoutes.admin.clients, label: t("navbar.admin.clients"), icon: Users, permission: PERMISSIONS.CUSTOMER_VIEW },
        ...(runtimeConfig?.['supervisor.selfservice.enabled'] === '1' ||
            runtimeConfig?.['supervisor.selfservice.enabled'] === 1 ||
            runtimeConfig?.['supervisor.selfservice.enabled'] === true ||
            runtimeConfig?.['supervisor.selfservice.enabled'] === 'true'
          ? [{
              key: "admin-self-service",
              to: AppRoutes.admin.selfServiceAccounts,
              label: t("navbar.admin.selfServiceAccounts"),
              icon: UserPlus,
              // Legacy nav.rb:71 gates Nav::Page.new(SelfserviceUser) on the
              // SelfserviceUser model's own view permission, not user_view.
              permission: PERMISSIONS.SELFSERVICE_USER_VIEW,
            }]
          : []),
        ...(showEligibility
          ? [{ key: "admin-eligibility", to: dynamic.eligibility, label: t("navbar.admin.eligibility"), icon: BookOpen, external: true }]
          : []),
        {
          key: "admin-trip-groups",
          to: AppRoutes.admin.tripGroups,
          label: t("navbar.admin.tripGroups"),
          icon: Map,
          permission: PERMISSIONS.TRIP_GROUP_VIEW,
        },
      ],
    },
    {
      key: "admin-funding-billing",
      to: "#",
      label: t("navbar.admin.fundingAndBilling"),
      submenu: [
        {
          key: "admin-billing-codes",
          to: AppRoutes.admin.billing.billingCodes,
          label: t("navbar.admin.billingCodes"),
          icon: CreditCard,
          permission: PERMISSIONS.FARE_BILLING_CODE_VIEW,
        },
        {
          key: "admin-billing-code-sets",
          to: AppRoutes.admin.billing.billingCodeSets,
          label: t("navbar.admin.billingCodeSets"),
          icon: Database,
          permission: PERMISSIONS.BILLING_CODE_SET_VIEW,
        },
        {
          key: "admin-funding-sources",
          to: AppRoutes.admin.billing.fundingSources,
          label: t("navbar.admin.fundingSources"),
          icon: CreditCard,
          permission: PERMISSIONS.FUNDING_SOURCE_VIEW,
        },
        {
          key: "admin-trip-purpose",
          to: AppRoutes.admin.billing.tripPurpose,
          label: t("navbar.admin.tripPurposes"),
          icon: ClipboardList,
          permission: PERMISSIONS.PURPOSE_VIEW,
        },
        {
          key: "admin-provider-rates",
          to: AppRoutes.admin.billing.providerRates,
          label: t("navbar.admin.providerRates"),
          icon: SlidersHorizontal,
          permission: PERMISSIONS.PROVIDER_RATE_VIEW,
        },
        ...(negotiatedProviderRatesEnabled
          ? [{
              key: "admin-surcharges",
              to: AppRoutes.admin.billing.surcharges,
              label: t("navbar.admin.surcharges"),
              icon: Flag,
              permission: PERMISSIONS.SURCHARGE_VIEW,
            }]
          : []),
      ],
    },
    {
      key: "admin-configuration",
      to: "#",
      label: t("navbar.admin.configuration"),
      submenu: [
        ...(runtimeConfig?.['supervisor.admin.tab.fixroute.disabled'] === '0' ||
            runtimeConfig?.['supervisor.admin.tab.fixroute.disabled'] === 0 ||
            runtimeConfig?.['supervisor.admin.tab.fixroute.disabled'] === false ||
            runtimeConfig?.['supervisor.admin.tab.fixroute.disabled'] === 'false'
          ? [{
              key: "admin-fixed-routes",
              to: AppRoutes.admin.fixedRoutes,
              label: t("navbar.admin.fixedRoutes"),
              icon: MapPinned,
              permission: PERMISSIONS.TEMPLATE_ROUTE_VIEW,
            }]
          : []),
        ...(inspectionEnabled
          ? [
              {
                key: "admin-inspection-forms",
                to: AppRoutes.admin.inspections,
                label: t("navbar.admin.inspectionForms"),
                icon: ClipboardList,
                permission: PERMISSIONS.INSPECTION_CHECKLIST_TEMPLATE_VIEW,
              },
              {
                key: "admin-provider-inspection-forms",
                to: AppRoutes.admin.providerInspectionForms,
                label: t("navbar.admin.providerInspectionForms"),
                icon: ClipboardList,
                permission: PERMISSIONS.INSPECTION_CHECKLIST_TEMPLATE_VIEW,
              },
              {
                key: "admin-vehicle-inspection-forms",
                to: AppRoutes.admin.vehicleInspectionForms,
                label: t("navbar.admin.vehicleInspectionForms"),
                icon: ClipboardList,
                permission: PERMISSIONS.INSPECTION_CHECKLIST_TEMPLATE_VIEW,
              },
            ]
          : []),
        {
          key: "admin-run-pools",
          to: AppRoutes.admin.runPools,
          label: t("navbar.admin.runPools"),
          icon: Route,
          permission: PERMISSIONS.RUN_POOL_VIEW,
        },
        {
          key: "admin-calendars",
          to: AppRoutes.admin.calendars,
          label: t("navbar.admin.calendars"),
          icon: CalendarRange,
          permission: PERMISSIONS.CALENDAR_VIEW,
        },
        {
          key: "admin-provider-deny-list",
          to: AppRoutes.admin.providerDenyList,
          label: t("navbar.admin.providerDenyList"),
          icon: Shield,
          permission: PERMISSIONS.COMPANY_BLACKLIST_VIEW,
        },
      ],
    },
  ],
  tools: [
    {
      key: "tools-data-export",
      to: "#",
      label: t("navbar.tools.dataExport"),
      submenu: [
        // {
        //   key: "tools-export-files",
        //   to: AppRoutes.tools.exportFiles,
        //   label: "Export Files",
        //   permission: PERMISSIONS.TRIP_EXPORT_OPERATION,
        // },
        {
          key: "tools-trip-export",
          to: AppRoutes.tools.tripExport,
          label: t("navbar.tools.tripExport"),
          icon: FileSpreadsheet,
          permission: PERMISSIONS.TRIP_EXPORT_OPERATION,
        },
        {
          key: "tools-archive-trip-export",
          to: AppRoutes.tools.archiveTripExport,
          label: t("navbar.tools.archiveTripExport"),
          icon: FolderArchive,
          permission: PERMISSIONS.TRIP_EXPORT_OPERATION,
        },
        {
          key: "tools-trip-export-profiles",
          to: AppRoutes.tools.tripExportProfiles,
          label: t("navbar.tools.tripExportProfiles"),
          icon: FileCog,
          permission: PERMISSIONS.TRIP_EXPORT_PROFILE_VIEW,
        },
      ],
    },
    {
      key: "tools-records",
      to: "#",
      label: t("navbar.tools.records"),
      submenu: [
        ...(runtimeConfig?.['supervisor.disable.feedback'] === '1' ||
            runtimeConfig?.['supervisor.disable.feedback'] === 1 ||
            runtimeConfig?.['supervisor.disable.feedback'] === true ||
            runtimeConfig?.['supervisor.disable.feedback'] === 'true'
          ? []
          : [{
              key: "tools-feedback",
              to: AppRoutes.tools.feedback,
              label: t("navbar.tools.feedback"),
              icon: MessageSquare,
              permission: PERMISSIONS.FEEDBACK_ISSUE_VIEW,
            }]),
        {
          key: "tools-roadcalls",
          to: AppRoutes.tools.roadcalls,
          label: t("navbar.tools.roadcalls"),
          icon: Bell,
          permission: PERMISSIONS.ROADCALL_VIEW,
        },
        ...(runtimeConfig?.['supervisor.disable.driverIncident'] === '1' ||
            runtimeConfig?.['supervisor.disable.driverIncident'] === 1 ||
            runtimeConfig?.['supervisor.disable.driverIncident'] === true ||
            runtimeConfig?.['supervisor.disable.driverIncident'] === 'true'
          ? []
          : [{
              key: "tools-driver-incidents",
              to: AppRoutes.tools.driverIncidents,
              label: t("navbar.tools.driverIncidents"),
              icon: AlertTriangle,
              permission: PERMISSIONS.DRIVER_INCIDENT_VIEW,
            }]),
        {
          key: "tools-vehicle-maintenance",
          to: AppRoutes.tools.vehicleMaintenance,
          label: t("navbar.tools.vehicleMaintenance"),
          icon: Wrench,
          permission: PERMISSIONS.VEHICLE_ADDON_VIEW,
        },
        {
          key: "tools-driver-additional-info",
          to: AppRoutes.tools.driverAdditionalInfo,
          label: t("navbar.tools.driverAdditionalInfo"),
          icon: UserCog,
          permission: PERMISSIONS.DRIVER_ADDON_VIEW,
        },
      ],
    },
    {
      // Bulk Import is placed here under "Other Tools" to consolidate all
      // external-link tools into a single submenu and reduce overall nav
      // footprint. The link target (dynamic.bulkImport) is unchanged.
      // Users with ORDERIMPORT_ACCESS will find it under Tools → Other Tools.
      key: "tools-other",
      to: "#",
      label: t("navbar.tools.otherTools"),
      submenu: [
        ...(runtimeConfig?.['supervisor.orderimport.enabled'] === '1' ||
            runtimeConfig?.['supervisor.orderimport.enabled'] === 1 ||
            runtimeConfig?.['supervisor.orderimport.enabled'] === true ||
            runtimeConfig?.['supervisor.orderimport.enabled'] === 'true'
          ? [{
              key: "tools-bulk-import",
              to: dynamic.bulkImport,
              external: true,
              icon: Upload,
              label: t("navbar.tools.bulkImport"),
              permission: PERMISSIONS.ORDERIMPORT_ACCESS,
            }]
          : []),
        ...(noShowEnabled
          ? [{
              key: "tools-rider-policy-violations",
              to: AppRoutes.tools.riderPolicyViolations,
              label: t("navbar.tools.riderPolicyViolations"),
              icon: Shield,
              permission: PERMISSIONS.RIDER_POLICY_VIOLATION_VIEW,
            }]
          : []),
        { key: "tools-reporting", to: dynamic.reporting, label: t("navbar.tools.reporting"), icon: BarChart2, permission: PERMISSIONS.REPORTING_ACCESS, external: true },
        ...(runtimeConfig?.['supervisor.feature.ediservice'] === '1' || runtimeConfig?.['supervisor.feature.ediservice'] === 1
          ? [{ key: "tools-edi", to: dynamic.edi, label: t("navbar.tools.edi"), icon: Globe, permission: PERMISSIONS.EDISERVICE_ACCESS, external: true }]
          : []),
        { key: "tools-insights", to: dynamic.insights, label: t("navbar.tools.insights"), icon: BarChart2, permission: PERMISSIONS.INSIGHT_VIEW, external: true },
      ],
    },
  ],
  system: [
    {
      key: "sys-location-settings",
      to: "#",
      label: t("navbar.system.locationSettings"),
      submenu: [
        { key: "sys-locations", to: AppRoutes.system.locations, label: t("navbar.system.locations"), icon: MapPinned, permission: PERMISSIONS.LOCATION_VIEW },
        {
          key: "sys-service-areas",
          to: AppRoutes.system.serviceAreas,
          label: t("navbar.system.serviceAreas"),
          icon: Map,
          permission: PERMISSIONS.SERVICE_AREA_VIEW,
        },
        {
          key: "sys-polygons",
          to: AppRoutes.system.polygons,
          label: t("navbar.system.polygons"),
          icon: MapPinned,
          permission: PERMISSIONS.ENCLOSURE_VIEW,
        },
      ],
    },
    {
      key: "sys-user-management",
      to: "#",
      label: t("navbar.system.userManagement"),
      submenu: [
        { key: "sys-users", to: AppRoutes.system.users, label: t("navbar.system.users"), icon: Users, permission: PERMISSIONS.USER_VIEW },
        { key: "sys-user-roles", to: AppRoutes.system.userRoles, label: t("navbar.system.userRoles"), icon: UserCog, permission: PERMISSIONS.USER_ROLE_VIEW },
        { key: "sys-centers", to: AppRoutes.system.centers, label: t("navbar.system.centers"), icon: Users, permission: PERMISSIONS.CLIENT_POOL_VIEW },
        { key: "sys-user-preferences", to: AppRoutes.system.userPreferences, label: t("navbar.system.userPreferences"), icon: Settings2, permission: PERMISSIONS.USER_UPDATE, allowedUserTypes: [4] },
        { key: "sys-filter-sets", to: AppRoutes.system.filterSet, label: t("navbar.system.filterSets"), icon: SlidersHorizontal, permission: PERMISSIONS.USER_FILTER_VIEW },
      ],
    },
    {
      key: "sys-devices",
      to: "#",
      label: t("navbar.system.devices"),
      submenu: [
        { key: "sys-devices", to: AppRoutes.system.devices, label: t("navbar.system.mobilityDevices"), icon: Database, permission: PERMISSIONS.DEVICE_VIEW },
        {
          key: "sys-mdt-devices",
          to: AppRoutes.system.mdtDevices,
          label: t("navbar.system.mdtDevices"),
          icon: Database,
          permission: PERMISSIONS.MDT_DEVICE_VIEW,
        },
        {
          key: "sys-message-templates",
          to: AppRoutes.system.messageTemplates,
          label: t("navbar.system.messageTemplates"),
          icon: MessageSquare,
          permission: PERMISSIONS.CANNED_MESSAGE_VIEW,
        },
      ],
    },
    {
      key: "sys-events",
      to: "#",
      label: t("navbar.system.events"),
      submenu: [
        {
          key: "sys-user-events",
          to: AppRoutes.system.events.userEvents,
          label: t("navbar.system.userEvents"),
          icon: UserCog,
          permission: PERMISSIONS.USER_EVENT_VIEW,
        },
        {
          key: "sys-schedule-events",
          to: AppRoutes.system.events.scheduleEvents,
          label: t("navbar.system.scheduleEvents"),
          icon: CalendarRange,
          permission: PERMISSIONS.ROUTE_EVENT_VIEW,
        },
        {
          key: "sys-trip-events",
          to: AppRoutes.system.events.tripEvents,
          label: t("navbar.system.tripEvents"),
          icon: Route,
          permission: PERMISSIONS.TRIP_EVENT_VIEW,
        },
        {
          key: "sys-subscription-events",
          to: AppRoutes.system.events.subscriptionEvents,
          label: t("navbar.system.subscriptionEvents"),
          icon: ListChecks,
          permission: PERMISSIONS.SUBSCRIPTION_VIEW,
        },
        {
          key: "sys-vehicle-events",
          to: AppRoutes.system.events.vehicleEvents,
          label: t("navbar.system.vehicleEvents"),
          icon: Truck,
          permission: PERMISSIONS.ARCHIVE_VEHICLE_EVENT_VIEW,
        },
        ...(mileageReimbursementEnabled
          ? [{
              key: "sys-reimbursement-events",
              to: AppRoutes.system.events.reimbursementEvents,
              label: t("navbar.system.reimbursementEvents"),
              icon: CreditCard,
              permission: PERMISSIONS.REIMBURSEMENT_VIEW,
            }]
          : []),
        {
          key: "sys-batch-job-events",
          to: AppRoutes.system.events.batchJobEvents,
          label: t("navbar.system.batchJobEvents"),
          icon: Database,
          permission: PERMISSIONS.BATCH_JOB_VIEW,
        },
      ],
    },
    {
      key: "sys-config",
      to: "#",
      label: t("navbar.system.configuration"),
      submenu: [
        // Mirrors Rails nav.rb:187-189 — RestConfiguration (Config Params) is
        // hidden from subcontractor users (enabler !current_ability.subcontractor?).
        ...(!isSubcontractorUser(user)
          ? [{
              key: "sys-config-params",
              to: AppRoutes.system.configuration.parameters,
              label: t("navbar.system.configParams"),
              icon: Settings2,
              permission: PERMISSIONS.CONFIGURATION_VIEW,
            }]
          : []),
        ...(runtimeConfig?.['component.requiredField.enabled'] === '1' ||
            runtimeConfig?.['component.requiredField.enabled'] === 1 ||
            runtimeConfig?.['component.requiredField.enabled'] === true ||
            runtimeConfig?.['component.requiredField.enabled'] === 'true'
          ? [{
              key: "sys-required-fields",
              to: AppRoutes.system.configuration.requiredFields,
              label: t("navbar.system.requiredFields"),
              icon: ClipboardList,
              permission: PERMISSIONS.REQUIRED_FIELD_VIEW,
            }]
          : []),
        {
          key: "sys-custom-values",
          to: AppRoutes.system.configuration.customValues,
          label: t("navbar.system.customValues"),
          icon: SlidersHorizontal,
          permission: PERMISSIONS.ALLOWED_VALUE_SET_VIEW,
        },
        {
          key: "sys-schedule-custom-fields",
          to: AppRoutes.system.configuration.scheduleCustomFields,
          label: t("navbar.system.scheduleCustomFields"),
          icon: FileCog,
          permission: PERMISSIONS.METAROUTE_CUSTOM_FIELD_VIEW,
        },
        {
          key: "sys-report-templates",
          to: AppRoutes.system.configuration.reportTemplates,
          label: t("navbar.system.reportTemplates"),
          icon: BarChart2,
          permission: PERMISSIONS.REPORT_TEMPLATE_VIEW,
        },
        // Mirrors Rails nav.rb:195-197 — BatchJob is hidden from subcontractor
        // users (enabler !current_ability.subcontractor?).
        ...(!isSubcontractorUser(user)
          ? [{
              key: "sys-batch-jobs",
              to: AppRoutes.system.configuration.batchJobs,
              label: t("navbar.system.batchJobs"),
              icon: Database,
              permission: PERMISSIONS.BATCH_JOB_VIEW,
            }]
          : []),
      ],
    },
  ],
  user: [
    {
      key: "user-help-docs",
      label: t("navbar.user.viewDocumentation"),
      to: AppRoutes.user.viewDocumentations,
      external: true,
    },
    {
      key: "user-help-reports",
      label: t("navbar.user.reportHelpPages"),
      to: AppRoutes.user.report,
      external: true,
    },
    {
      key: "user-help-support",
      label: t("navbar.user.getSupport"),
      to: AppRoutes.user.getSupport,
      external: true,
    },
    {
      key: "user-feedback",
      label: t("navbar.user.sendFeedback"),
      to: "/tools/feedback",
    },
    { key: "divider-0", divider: true, to: "#" },
    {
      key: "user-language",
      label: t("general.language", "Language"),
      to: "#",
      icon: Globe,
      // HB3: testId allows test assertions on the language submenu trigger.
      // Globe icon provides visible affordance for non-English users that
      // language switching is accessible inside the profile menu.
      testId: "user-menu-language-switcher",
      submenu: [
        { key: "lang-en", to: "#", label: "English", testId: "lang-option-en" },
        { key: "lang-es", to: "#", label: "Español", testId: "lang-option-es" },
      ],
    },
    {
      key: "user-theme",
      label: t("navbar.user.theme", "Theme"),
      to: "#",
      icon: Monitor,
      submenu: [
        { key: "theme-light",  to: "#", label: "Light",  icon: Sun },
        { key: "theme-dark",   to: "#", label: "Dark",   icon: Moon },
        { key: "theme-system", to: "#", label: "System", icon: Monitor },
      ],
    },
    {
      key: "user-switch-version",
      label: t("navbar.user.switchToOlderVersion", "Switch to older version"),
      to: "#",
    },
    { key: "divider-1", divider: true, to: "#" },
    { key: "change-password", to: "#", label: t("navbar.user.changePassword") },
    { key: "view-settings", to: "#", label: t("navbar.user.viewSettings") },
    { key: "divider-2", divider: true, to: "#" },
    { key: "user-signout", to: AppRoutes.user.signOut, label: t("navbar.user.signOut") },
    { key: "divider-3", divider: true, to: "#" },
    {
      key: "user-version-info",
      to: "#",
      versionInfo: true,
      releaseNotesUrl: AppRoutes.user.releaseNotes,
    },
  ],
};
};
